import * as React from "react"
import { uploadProfilePhoto } from "../auth/api"
import Avatar from "./Avatar"

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB

export default function ProfilePhotoModal({ profile, onClose, onSaved }) {
  const [preview, setPreview] = React.useState(profile?.fotoPerfilUrl || null)
  const [file, setFile] = React.useState(null)
  const [error, setError] = React.useState("")
  const [loading, setLoading] = React.useState(false)
  const [success, setSuccess] = React.useState(false)
  const inputRef = React.useRef(null)

  // Fecha ao clicar fora
  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose()
  }

  // Fecha com Escape
  React.useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  // Libera a URL temporária de preview quando o componente desmontar ou trocar de arquivo
  React.useEffect(() => {
    return () => {
      if (preview && preview.startsWith("blob:")) {
        URL.revokeObjectURL(preview)
      }
    }
  }, [preview])

  function handleFileChange(e) {
    const selected = e.target.files?.[0]
    if (!selected) return

    if (!selected.type.startsWith("image/")) {
      setError("Selecione um arquivo de imagem válido.")
      return
    }
    if (selected.size > MAX_FILE_SIZE) {
      setError("A imagem deve ter no máximo 5MB.")
      return
    }

    setError("")
    setFile(selected)
    setPreview(URL.createObjectURL(selected))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!file) {
      setError("Selecione uma foto antes de salvar.")
      return
    }
    setError("")
    setSuccess(false)
    try {
      setLoading(true)
      const data = await uploadProfilePhoto(file)
      setSuccess(true)
      onSaved(data?.profile)
      setTimeout(() => onClose(), 1200)
    } catch (err) {
      setError(err?.message || "Não foi possível atualizar a foto.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}>
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-cor-base rounded-t-2xl">
          <h2 className="text-xl font-oswald font-semibold text-white tracking-wide">
            Foto de perfil
          </h2>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white text-2xl leading-none transition-colors"
            aria-label="Fechar"
            type="button">
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-6 grid gap-5">
          <div className="flex flex-col items-center gap-3">
            <Avatar src={preview} size="lg" />

            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="px-4 py-2 rounded-lg border-2 border-cor-base text-cor-base font-semibold text-sm active:scale-[.94] active:duration-75 transition-all hover:scale-[1.05]">
              Escolher imagem
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="image/png, image/jpeg, image/webp"
              onChange={handleFileChange}
              className="hidden"
            />
            <p className="text-xs text-slate-400 text-center">
              JPG, PNG ou WEBP · até 5MB
            </p>
          </div>

          {/* Feedback */}
          {error && (
            <p className="text-red-600 text-center text-sm font-semibold">
              {error}
            </p>
          )}
          {success && (
            <p className="text-green-600 text-center text-sm font-semibold">
              Foto atualizada com sucesso!
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-all">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !file}
              className="flex-1 py-2.5 rounded-xl bg-cor-base text-white font-semibold hover:scale-[1.02] active:scale-[.97] transition-all disabled:opacity-60 disabled:cursor-not-allowed">
              {loading ? "Enviando..." : "Salvar foto"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
